import { DeleteFilled, LoadingOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  APPROVE_TEAM_INVITE,
  DISCOVER_USERS,
  GET_IDEA,
  GET_ME,
  Idea,
  IdeaCollaboration,
  Me,
  TEAM_INVITE,
  TEAM_INVITE_DELETE,
  User,
} from '@frontend/data-access';
import { App, Badge, Button, Form, Modal, Select, Spin } from 'antd';
import useTranslation from 'next-translate/useTranslation';
import { useMemo, useState } from 'react';

import StatusTag from '../status-tag/status-tag';
import UserAvatar from '../user-avatar/user-avatar';

export function IdeaDetailTeamInviteButton({ data }: { data: Idea }) {
  const { notification } = App.useApp();
  const { t } = useTranslation('common');
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [memberId, setMemberId] = useState<number>();
  const { data: meData } = useQuery<{ me: Me }>(GET_ME);
  const [updateId, setUpdateId] = useState<string>();
  const [deleteId, setDeleteId] = useState<string>();
  const pendingRequest = useMemo(
    () =>
      data?.applicants.filter(
        (m) => m?.type === 'JOIN' && m?.status === 'PENDING'
      ),
    [data]
  );
  const [searchUser, { data: userData, loading: userLoading }] =
    useLazyQuery(DISCOVER_USERS);

  const handleSearch = (value: string) => {
    return searchUser({
      variables: {
        first: 1000,
        filterBy: {
          name: value.trim(),
          roles: ['ADMIN', 'INTRAPRENEUR'],
        },
      },
    });
  };

  const handleApproveCompleted = () => {
    notification.success({
      message: t('join_request_updated'),
    });
  };

  const [approve, { loading: loadingApprove }] = useMutation(
    APPROVE_TEAM_INVITE,
    {
      onCompleted: handleApproveCompleted,
      refetchQueries: [{ query: GET_IDEA, variables: { ideaId: data?.id } }],
    }
  );

  const [reject, { loading: loadingReject }] = useMutation(
    APPROVE_TEAM_INVITE,
    {
      onCompleted: handleApproveCompleted,
      refetchQueries: [{ query: GET_IDEA, variables: { ideaId: data?.id } }],
    }
  );

  const handleDeleteCompleted = () => {
    notification.success({
      message: t('team_member_deleted'),
    });
  };
  const [deleteRequest, { loading: loadingDelete }] = useMutation(
    TEAM_INVITE_DELETE,
    {
      onCompleted: handleDeleteCompleted,
      refetchQueries: [{ query: GET_IDEA, variables: { ideaId: data?.id } }],
    }
  );
  const handleInviteCompleted = () => {
    notification.success({
      message: t('invite_request_sent'),
    });
  };
  const [invite, { loading: loadingInvite }] = useMutation(TEAM_INVITE, {
    onCompleted: handleInviteCompleted,
    refetchQueries: [{ query: GET_IDEA, variables: { ideaId: data?.id } }],
  });

  const handleInviteClick = () => {
    if (memberId === meData?.me?.id) {
      notification.warning({
        message: t('unable_to_invite_yourself'),
      });
      return;
    }
    invite({
      variables: { ideaId: data?.id, userId: memberId, type: 'INVITE' },
    });
    setMemberId(undefined);
  };

  const InviteItem = ({
    applicant,
    isOwner,
  }: {
    applicant: IdeaCollaboration;
    isOwner?: boolean;
  }) => (
    <div className="grid grid-cols-2">
      <div className="flex items-center space-x-2">
        <UserAvatar user={applicant?.user} />
        <div>
          <div className="font-semibold">{applicant?.user?.fullName}</div>
          <div className="text-gray-700">{applicant?.user?.email}</div>
        </div>
      </div>
      <div className="flex justify-between items-center">
        <div>
          <StatusTag status={applicant?.status} />
        </div>
        <div className="space-x-2">
          {applicant?.type === 'JOIN' && applicant?.status === 'PENDING' ? (
            <>
              <Button
                size="small"
                type="primary"
                ghost
                shape="round"
                loading={loadingApprove && updateId === applicant?.id}
                onClick={() => {
                  setUpdateId(applicant?.id);
                  approve({
                    variables: {
                      id: applicant?.id,
                      status: 'APPROVED',
                    },
                  });
                }}
              >
                {t('accept')}
              </Button>
              <Button
                size="small"
                danger
                ghost
                shape="round"
                loading={loadingReject && updateId === applicant?.id}
                onClick={() => {
                  setUpdateId(applicant?.id);
                  reject({
                    variables: {
                      id: applicant?.id,
                      status: 'REJECTED',
                    },
                  });
                }}
              >
                {t('reject')}
              </Button>
            </>
          ) : (
            !isOwner && (
              <Button
                shape="circle"
                onClick={() => {
                  setDeleteId(applicant?.id);
                  deleteRequest({ variables: { id: applicant?.id } });
                }}
              >
                {loadingDelete && deleteId === applicant?.id ? (
                  <LoadingOutlined />
                ) : (
                  <DeleteFilled />
                )}
              </Button>
            )
          )}
        </div>
      </div>
    </div>
  );

  return meData?.me?.id === data?.createdBy?.id ? (
    <>
      <Badge count={pendingRequest.length}>
        <Button size="small" shape="round" onClick={() => setIsModalOpen(true)}>
          {t('team_management')}
        </Button>
      </Badge>
      <Modal
        title={t('teammate_invites')}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        width={690}
        footer={false}
      >
        <div className="space-y-4">
          {data?.collaborative && (
            <Form layout="inline">
              <Form.Item className="!grow">
                <Select
                  showSearch
                  allowClear
                  placeholder={t('search_to_select')}
                  loading={userLoading}
                  notFoundContent={
                    userLoading ? (
                      <div className="text-center">
                        <Spin size="small" />
                      </div>
                    ) : null
                  }
                  filterOption={(input, option) =>
                    ((option?.label as string) ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  onChange={(value: string) => {
                    setMemberId(parseInt(value));
                  }}
                  onSearch={handleSearch}
                >
                  {userData?.discoverUsers?.edges?.map(
                    (item: { node: User }) => (
                      <Select.Option
                        key={item.node.id}
                        value={item.node.id as unknown as string}
                        label={
                          (item.node?.firstName || '') +
                          (item.node?.lastName || '')
                        }
                      >
                        <div className="flex items-center gap-2">
                          <UserAvatar
                            size={24}
                            user={{
                              ...item.node,
                              image: {
                                url: item.node?.image as string,
                              },
                            }}
                          />
                          {item.node.firstName + ' ' + item.node.lastName}
                        </div>
                      </Select.Option>
                    )
                  )}
                </Select>
              </Form.Item>
              <Form.Item>
                <Button
                  loading={loadingInvite}
                  disabled={memberId === undefined}
                  shape="round"
                  onClick={handleInviteClick}
                >
                  {t('invite')}
                </Button>
              </Form.Item>
            </Form>
          )}
          {data?.applicants
            .filter(
              (m) =>
                m?.type === 'INVITE' ||
                (m?.type === 'JOIN' && m?.status !== 'PENDING')
            )
            .map((member, index) => (
              <InviteItem
                key={index}
                applicant={member}
                isOwner={data?.createdBy?.id === member?.userId}
              />
            ))}
          {pendingRequest.length > 0 && (
            <>
              <div className="font-semibold">{t('join_requests')}</div>
              {pendingRequest.map((member, index) => (
                <InviteItem key={index} applicant={member} />
              ))}
            </>
          )}
        </div>
      </Modal>
    </>
  ) : null;
}
